import type { OrdemServico } from '../types/ordemServico';
import { sendZplToDefaultPrinter } from './zebraBrowserPrint';

export interface EtiquetaData {
  numeroNota: string;
  nome: string;
  remocao: string;
  clinica: string;
  velorio: string;
  cemiterio: string;
}

const LOGO_URL = '/logos/consolare.svg';

/** Largura/altura próximos do exemplo (~57mm x 40mm @ 203 dpi) */
const LABEL_WIDTH = 460;
const LABEL_HEIGHT = 320;

/**
 * Salto no topo da etiqueta: evita o logo ser cortado na borda/gap do rolo.
 * Ajuste fino em dots (203 dpi ≈ 8 dots/mm).
 */
const TOP_SKIP = 40;

/** Layout: QR grande, texto colado e até a borda direita */
const QR_X = 6;
const QR_Y = 58;
const QR_MAG = 5;
/** Tamanho aproximado do QR (^BQ modelo 2, versão 1 + quiet zone) */
const QR_SIZE = 29 * QR_MAG; // 145 dots
const TEXT_X = 158;
const TEXT_Y = 58;
const FONT_H = 22;
const FONT_W = 16;
const LINE_H = 23;
const RIGHT_MARGIN = 2;

/** Título + conteúdo: no máximo 36 caracteres por linha */
const MAX_LINE_LEN = 36;

const sanitize = (value: string): string =>
  (value || '')
    .replace(/\s+/g, ' ')
    .replace(/[\^~]/g, ' ')
    .trim();

const fullValue = (value: string): string => {
  const clean = sanitize(value);
  if (!clean || clean.toLowerCase() === 'null') return '-';
  return clean.toUpperCase();
};

/** Monta a linha e corta em 36 chars (título + endereço/nome) */
const formatLine = (titulo: string, valor: string): string => {
  const prefix = `${titulo}: `;
  const content = fullValue(valor);
  const line = `${prefix}${content}`;
  return line.length > MAX_LINE_LEN ? line.slice(0, MAX_LINE_LEN) : line;
};

const getFaseEndereco = (os: OrdemServico, nomeFase: string): string => {
  const fase = os.fases?.find((f) => f.nome_fase === nomeFase);
  if (!fase) return '-';
  const endereco = (fase.endereco_destino || '').trim();
  if (endereco) return endereco;
  return (fase.nome_local || '').trim() || '-';
};

export const buildEtiquetaDataFromOS = (os: OrdemServico): EtiquetaData => ({
  numeroNota: String(os.numero_nota_contratacao || os._DADOSOS_NumOrdem || '').trim(),
  nome: (os.nome_falecido || '').trim(),
  remocao: getFaseEndereco(os, 'Remoção'),
  clinica: getFaseEndereco(os, 'Clínica'),
  velorio: getFaseEndereco(os, 'Velório'),
  cemiterio: getFaseEndereco(os, 'Sepultamento')
});

const toHexByte = (value: number): string =>
  value.toString(16).toUpperCase().padStart(2, '0');

const canvasToZplGraphic = (
  canvas: HTMLCanvasElement,
  x: number,
  y: number
): string => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;
  let hex = '';

  for (let row = 0; row < height; row++) {
    for (let byteIndex = 0; byteIndex < bytesPerRow; byteIndex++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const col = byteIndex * 8 + bit;
        if (col >= width) continue;
        const idx = (row * width + col) * 4;
        const r = imageData.data[idx];
        const g = imageData.data[idx + 1];
        const b = imageData.data[idx + 2];
        const a = imageData.data[idx + 3];
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        if (a > 128 && luminance < 180) {
          byte |= 1 << (7 - bit);
        }
      }
      hex += toHexByte(byte);
    }
  }

  return `^FO${x},${y}^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hex}^FS`;
};

const loadLogoGraphic = async (
  x: number,
  y: number,
  targetWidth: number,
  targetHeight: number
): Promise<string> => {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Falha ao carregar logo'));
      image.src = LOGO_URL;
    });

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    return canvasToZplGraphic(canvas, x, y);
  } catch {
    return `^FO${x},${y}^A0N,18,14^FB${targetWidth},1,0,C^FDconsolare^FS`;
  }
};

export const buildEtiquetaZpl = async (
  data: EtiquetaData,
  quantidade: 1 | 5
): Promise<string> => {
  const notaRaw = sanitize(data.numeroNota);
  if (!notaRaw) {
    throw new Error('Número da Nota de Contratação não encontrado nesta OS');
  }

  // Logo no topo (centralizado)
  const logoTopWidth = 160;
  const logoTopHeight = 40;
  const logoTopX = Math.round((LABEL_WIDTH - logoTopWidth) / 2);
  const logoTop = await loadLogoGraphic(logoTopX, 8, logoTopWidth, logoTopHeight);

  // Logo também abaixo do QR, com a mesma largura do QR
  const logoBelowWidth = QR_SIZE;
  const logoBelowHeight = Math.max(28, Math.round(QR_SIZE * (40 / 160)));
  const logoBelowY = QR_Y + QR_SIZE + 4;
  const logoBelow = await loadLogoGraphic(
    QR_X,
    logoBelowY,
    logoBelowWidth,
    logoBelowHeight
  );

  const textBlockWidth = LABEL_WIDTH - TEXT_X - RIGHT_MARGIN;
  let y = TEXT_Y;

  const textLines = [
    formatLine('Nº Nota', notaRaw),
    formatLine('Nome', data.nome),
    formatLine('Remoção', data.remocao),
    formatLine('Clínica', data.clinica),
    formatLine('Velório', data.velorio),
    formatLine('Cemitério', data.cemiterio)
  ];

  const textCommands = textLines
    .map((line) => {
      const cmd = `^FO${TEXT_X},${y}^A0N,${FONT_H},${FONT_W}^FB${textBlockWidth},1,0,L^FD${line}^FS`;
      y += LINE_H;
      return cmd;
    })
    .join('\n');

  const qr = `^FO${QR_X},${QR_Y}^BQN,2,${QR_MAG}^FDQA,${notaRaw}^FS`;

  return `^XA
^CI28
^PW${LABEL_WIDTH}
^LL${LABEL_HEIGHT}
^LH0,0
^LT0,${TOP_SKIP}
^PR2
${logoTop}
${qr}
${logoBelow}
${textCommands}
^PQ${quantidade},0,1,Y
^XZ`;
};

export const imprimirEtiquetasOS = async (
  os: OrdemServico,
  quantidade: 1 | 5
): Promise<void> => {
  const data = buildEtiquetaDataFromOS(os);
  const zpl = await buildEtiquetaZpl(data, quantidade);
  await sendZplToDefaultPrinter(zpl);
};
